import { useState, useEffect, useMemo, useCallback } from 'react'
import { format } from 'date-fns'
import { IPalletDetails } from '../../../../modules/API/Pallets/models/palletDetails'
import { Pallets } from '../../../../modules/API/Pallets/pallets'
import { Market } from '../../../../modules/API/Market/market'
import { IPostProduct, IUpdateProduct } from '../../../../modules/API/Market/models/marketRequests'
import { Certificates } from '../../../../modules/API/Certificates/certificates'
import { ICreateCertFromEntryPayload } from '../../../../modules/API/Certificates/models/createCertFromEntry'
import { IEntriesKnisotItem } from '../../../../modules/API/Knisot/models/entrie'
import { handleNotifications } from '../../../utils/handleNotifications'

export type DrawerMode = 'create' | 'edit'

export interface DrawerState {
  isOpen: boolean
  mode: DrawerMode
  detail: IPalletDetails | null
}

export interface IPalletGroup {
  palletNum: number
  customerName: string
  deliveryNum: number
  deliverySub: number
  items: IPalletDetails[]
}

export interface PalletDetailFormData {
  customerID: number
  salePrice: number
  weightNeto: number
  quantity: number
  remarks: string
}

const INITIAL_DRAWER: DrawerState = {
  isOpen: false,
  mode: 'create',
  detail: null,
}

const useEntryPallets = (entryItem: IEntriesKnisotItem) => {
  const [pallets, setPallets] = useState<IPalletDetails[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [drawerState, setDrawerState] = useState<DrawerState>(INITIAL_DRAWER)

  const fetchPallets = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await Pallets.getPalletsByEntry(entryItem.EntryNum, entryItem.DetailNum)
      setPallets(data)
    } catch {
      setError('שגיאה בטעינת נתונים')
    } finally {
      setIsLoading(false)
    }
  }, [entryItem.EntryNum, entryItem.DetailNum])

  useEffect(() => {
    fetchPallets()
  }, [fetchPallets])

  const filteredPallets = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return pallets
    return pallets.filter((p) =>
      `${p.ProductDesc.trim()} ${p.VarietyDesc.trim()}`.toLowerCase().includes(q)
    )
  }, [pallets, searchQuery])

  const palletGroups = useMemo((): IPalletGroup[] => {
    const grouped = filteredPallets.reduce<Record<number, IPalletGroup>>((acc, detail) => {
      const group = acc[detail.Pallet_Num]
      if (group) {
        group.items.push(detail)
      } else {
        acc[detail.Pallet_Num] = {
          palletNum: detail.Pallet_Num,
          customerName: detail.Customer_Name,
          deliveryNum: detail.Delivery_Num,
          deliverySub: detail.Delivery_Sub,
          items: [detail],
        }
      }
      return acc
    }, {})

    return Object.values(grouped)
  }, [filteredPallets])

  const handleRowClick = useCallback((detail: IPalletDetails) => {
    setDrawerState({ isOpen: true, mode: 'edit', detail })
  }, [])

  const handleOpenCreate = useCallback(() => {
    setDrawerState({ isOpen: true, mode: 'create', detail: null })
  }, [])

  const handleCloseDrawer = useCallback(() => {
    setDrawerState((prev) => ({ ...prev, isOpen: false }))
  }, [])

  /**
   * Creates a new certificate via Certificates.createCertFromEntry.
   * The IPostProduct line is built from entryItem (structure) + form (qty/weight/price/customer).
   * The resulting line is wrapped in PalletDetails array of the certificate payload.
   */
  const handleCreate = useCallback(
    async (formData: PalletDetailFormData) => {
      const palletLine: IPostProduct = {
        EntryNum: `${entryItem.EntryNum}-${entryItem.DetailNum}`,
        Quality: 0,
        AreaNum: 0,
        WarehouseID: entryItem.Tranzit,
        GrowerID: 0,
        ProductID: entryItem.ProductID,
        VarietyID: entryItem.VarietyID,
        PalletNum: 999999,
        OrderNum: 0,
        Qty: formData.quantity,
        Weight: formData.weightNeto,
        PalletType: entryItem.TypePallet,
        OrderRowRecID: 0,
        CustomerID: formData.customerID,
        ScanProblem: 0,
        ChangeReason: 0,
        PackID: entryItem.TypePack,
        Reseller_Code: 0,
        SalePrice: formData.salePrice,
        Type_SaleCalculation: 0,
        Type_BuyCalculation: 0,
        Remarks: formData.remarks,
        SizeID: entryItem.SizeDesc,
        PalletsQty: 1,
        PackID_Secondary: 0,
        Quantity_Packs_Main: 0,
        Reference_Delivery_Entry: 0,
      }

      const certPayload: ICreateCertFromEntryPayload = {
        CustomerID: formData.customerID,
        DeliveryDate: format(new Date(), 'yyyy-MM-dd'),
        DeliveryTime: format(new Date(), 'HH:mm'),
        OrderNum: 0,
        PalletDetails: [palletLine],
        Tranzit: entryItem.Tranzit,
        Vehicle_Code: 0,
      }

      try {
        await Certificates.createCertFromEntry(certPayload)
        handleNotifications('תעודה נוצרה בהצלחה', { type: 'success' })
        handleCloseDrawer()
        fetchPallets()
      } catch {
        handleNotifications('שגיאה ביצירת תעודה', { type: 'error' })
      }
    },
    [entryItem, handleCloseDrawer, fetchPallets]
  )

  /** Updates pallet detail via Market.updateProduct with full IUpdateProduct payload. */
  const handleUpdate = useCallback(
    async (detail: IPalletDetails, formData: PalletDetailFormData) => {
      const payload: IUpdateProduct = {
        PackID: detail.PackID,
        Pallet_Type: detail.Pallet_Type,
        ProductID: entryItem.ProductID,
        VarietyID: entryItem.VarietyID,
        SizeID: detail.SizeID,
        Quantity: formData.quantity,
        Quantity_Pallets: 0,
        SalePrice: formData.salePrice,
        WeightNeto: formData.weightNeto,
        WeightBuy: formData.weightNeto,
        BuyPrice: detail.BuyPrice,
        WeightBrutto: 0,
        Type_SaleCalculation: detail.Type_SaleCalculation,
        Type_BuyCalculation: detail.Type_BuyCalculation,
        Remarks: formData.remarks,
        isIncludePallet: false,
        Reference_Delivery_Entry: 0,
      }
      try {
        await Market.updateProduct(detail.RecID, payload)
        handleNotifications('משטח עודכן בהצלחה', { type: 'success' })
        handleCloseDrawer()
        fetchPallets()
      } catch {
        handleNotifications('שגיאה בעדכון משטח', { type: 'error' })
      }
    },
    [entryItem.ProductID, entryItem.VarietyID, handleCloseDrawer, fetchPallets]
  )

  return {
    isLoading,
    error,
    palletGroups,
    searchQuery,
    setSearchQuery,
    drawerState,
    handleRowClick,
    handleOpenCreate,
    handleCloseDrawer,
    handleCreate,
    handleUpdate,
  }
}

export default useEntryPallets
